import React, { useState } from 'react';
import { Settings as SettingsIcon, User, Send, MessageSquare, Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  SidebarTrigger,
} from '@nimbitlabs/ui';
import { NotificationBell, SendNotificationModal, FeedbackModal, type NotificationData, useNotifications } from '@nimbitlabs/features';
import { useAuth } from '@nimbitlabs/auth';
import { AccountSwitcher } from './AccountSwitcher';

interface TopNavigationProps {
  appName: string;
  onFeedbackSubmit?: (feedback: { type: string; message: string; email?: string }) => Promise<void>;
}

export function TopNavigation({ appName, onFeedbackSubmit }: TopNavigationProps) {
  const { user, logout, switchAccount, loadAccounts } = useAuth();
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [accounts, setAccounts] = useState<any[]>([]);
  const { notifications, unreadCount, isLoading, markAsRead, markAllAsRead, deleteNotification } = useNotifications();

  const handleLoadAccounts = async () => {
    const accountsData = await loadAccounts();
    // Map API response to AccountSwitcher format
    const mappedAccounts = (accountsData || []).map((account: any) => ({
      id: account.accountId,
      name: account.companyName,
      role: account.role,
      department: account.department,
    }));
    setAccounts(mappedAccounts);
    return mappedAccounts;
  };

  const handleSwitchAccount = async (accountId: string) => {
    await switchAccount(accountId);
  };

  return (
    <header className="fixed top-0 left-0 right-0 h-16 w-full bg-sidebar/80 backdrop-blur-sm flex items-center justify-between px-3 md:px-6 shadow-soft z-50 border-b">
      <div className="flex items-center gap-2 md:gap-4 min-w-0">
        <SidebarTrigger />
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 bg-gradient-to-br from-primary to-primary/70 rounded-lg flex items-center justify-center shrink-0">
            <span className="text-primary-foreground font-bold text-sm">
              {appName?.charAt(0).toUpperCase() || 'A'}
            </span>
          </div>
          <span className="font-semibold hidden sm:inline truncate">{appName}</span>
        </div>
      </div>

      <div className="flex items-center gap-1 md:gap-2 min-w-0">
        <div className="hidden lg:block">
          <AccountSwitcher 
            currentAccountId={user?.accountId}
            currentAccountName={user?.companyName}
            onLoadAccounts={handleLoadAccounts}
            onSwitchAccount={handleSwitchAccount}
          />
        </div>

        <NotificationBell 
          notifications={notifications}
          unreadCount={unreadCount}
          isLoading={isLoading}
          onMarkAsRead={markAsRead}
          onMarkAllAsRead={markAllAsRead}
          onDelete={deleteNotification}
        />

        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => setSendModalOpen(true)}
          title="Send notification"
          className="hidden md:flex"
        >
          <Send className="h-5 w-5" />
        </Button>

        <Button 
          variant="ghost" 
          size="icon"
          asChild
          title="Settings"
          className="hidden md:flex"
        >
          <Link to="/settings">
            <SettingsIcon className="h-5 w-5" />
          </Link>
        </Button>

        <div className="hidden md:block">
          <FeedbackModal onSubmit={onFeedbackSubmit} trigger={
            <Button
              variant="ghost"
              size="icon"
              title="Send Feedback"
            >
              <MessageSquare className="h-5 w-5" />
            </Button>
          } />
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              size="sm" 
              className="relative h-9 w-9 rounded-full border border-primary/20"
            >
              <User className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end" forceMount>
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none">{user?.name}</p>
                <p className="text-xs leading-none text-muted-foreground">
                  {user?.email}
                </p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            
            {/* Show account switcher on mobile */}
            <div className="lg:hidden">
              {accounts.length > 0 && (
                <>
                  <DropdownMenuLabel className="text-xs text-muted-foreground">
                    Switch Account
                  </DropdownMenuLabel>
                  {accounts.map((account) => (
                    <DropdownMenuItem
                      key={account.id}
                      onClick={() => handleSwitchAccount(account.id)}
                      className="flex items-center gap-2"
                    >
                      <Building2 className="h-4 w-4" />
                      <div className="flex flex-col">
                        <span className="text-sm">{account.name}</span>
                        {account.role && (
                          <span className="text-xs text-muted-foreground">{account.role}</span>
                        )}
                      </div>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                </>
              )}
            </div>
            
            <DropdownMenuItem asChild>
              <Link to="/settings">Profile</Link>
            </DropdownMenuItem>
            
            {/* Show mobile-only menu items */}
            <div className="md:hidden">
              <DropdownMenuItem asChild>
                <Link to="/settings" className="flex items-center gap-2">
                  <SettingsIcon className="h-4 w-4" />
                  Settings
                </Link>
              </DropdownMenuItem>
            </div>
            
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout}>
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <SendNotificationModal 
        open={sendModalOpen} 
        onOpenChange={setSendModalOpen}
      />
    </header>
  );
}
